import { useState, useEffect, useLayoutEffect, Suspense, useRef, useMemo, useCallback } from 'react';
import { createPortal } from 'react-dom';
import debug from 'debug';

import SpinnerText from '../SpinnerText/SpinnerText';
import { api } from '../../api/client';
import qs from 'qs';
import './PaymentApplication.scss';
import * as actionCreators from '../../store/actions/login';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import styled from '@emotion/styled';
import apiHelper from '../../api/apiHelper';
import PaymentSettings from '../PaymentSettings/PaymentSettings';
import Auth from '../../api/auth';
import useTokenAuthentication from '../../hooks/useTokenAuthentication';
import { STEP_STATUS, AUTH_ERROR_TYPES } from '../../constants/authConstants';
import {
  InvalidTokenScreen,
  ExpiredTokenScreen,
  MissingTokenScreen,
} from '../Auth/AuthErrorScreens';
import { serverLogger } from '../../libs/datadog-logger/loggers/serverLogger';
import {
  appendCidToUrl,
  clearActiveCorrelationId,
  getCorrelationId,
  setActiveCorrelationId,
} from '../../libs/correlationId';
import { defaultNetworkErrorMessage } from '../../config';

import { DD_PAYMENT_APP_EVENTS } from '../../auth/datadogEvents';
import { buildDocumentSignPayload } from './documentSignPayload';
import {
  describeEntrySource,
  directDebitRequiredMessage,
  envelopeReturnedMessage,
  pageOpenedMessage,
  paymentMethodAddedMessage,
  paymentMethodAddedNotFoundMessage,
  selectionMessage,
  signRejectedMessage,
} from './mopLogMessages';

// Create debug namespaces for payment application
const debugAuth = debug('auth:payment-app');
const debugApi = debug('auth:payment-app:api');
const debugError = debug('auth:error');
const debugApp = debug('auth:payment-app:flow');

/**
 * Sanitizes error response data for safe logging.
 * Extracts only safe fields and truncates messages to prevent
 * logging sensitive payment data or bloating logs.
 */
const sanitizeErrorDetails = (error) => {
  const safeErrorDetails = error.response?.data ? {
    message: error.response.data.Message || error.response.data.message,
    errorCode: error.response.data.ErrorCode || error.response.data.errorCode || error.response.data.code,
    statusText: error.response.statusText,
  } : { message: error.message };

  // Truncate message to prevent log bloat (max 500 chars)
  if (safeErrorDetails.message && safeErrorDetails.message.length > 500) {
    safeErrorDetails.message = safeErrorDetails.message.substring(0, 500) + '... [truncated]';
  }

  return safeErrorDetails;
};

const BorderBox = styled.div`
  margin-top: 2rem;
  border: 2px solid lightgray;
  padding: 2rem;
`;

const WarningLabel = styled.span`
  color: #317f04;
  font-weight: 600;
  font-size: 13px;
`;
const frameStyle = {
  width: '100vw',
  height: '100vh',
};

const APPROVED_SESSION_EXPIRED_MESSAGE = 'Your session has expired. Please refresh the page and try again.';
const APPROVED_PAYMENT_SERVICE_UNAVAILABLE_MESSAGE =
  'Unable to reach the payment service. Please try again in a few minutes.';

const PaymentApplication = ({ location, history }) => {
  document.title = 'TimePayment ExpressLease\u2122';

  const params = qs.parse(location.search, { ignoreQueryPrefix: true });

  // Use authentication hook
  const {
    authError,
    authErrorType,
    authStepOneStatus,
    userName,
    isAuthenticating,
    loadProgress: authLoadProgress,
    loadStep: authLoadStep,
  } = useTokenAuthentication({ key: params.key, token: params.token });

  const [isLoading, setIsLoading] = useState(false);

  const [isAutoPay, setIsAutoPay] = useState(true);

  const [automaticPaymentMessage, setAutomaticPaymentMessage] = useState('');
  const [amountDueAtSigningMessage, setAmountDueAtSigningMessage] = useState('');
  const [isDueAtSigningGreaterThanZero, setIsDueAtSigningGreaterThanZero] = useState(false);

  const [selectedQuote, setSelectedQuote] = useState(null);

  const [isDirectDebitRequired, setIsDirectDebitRequired] = useState(false);
  const [serverError, setServerError] = useState('');
  const [errorContext, setErrorContext] = useState(null);
  const [docusignUrl, setDocusignUrl] = useState('');
  const [documentGenerating, setDocumentGenerating] = useState(false);
  const [processingFeeOption, setProcessingFeeOption] = useState('lessee');
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState(null);
  const [formError, setFormError] = useState(null);
  // New: track submit button action separately
  const [isSubmitting, setIsSubmitting] = useState(false);
  // Progress indicator state for data loading (auth uses hook's progress)
  const [loadProgress, setLoadProgress] = useState(0);
  const [loadStep, setLoadStep] = useState('');
  const applicationNumber = params.app;
  const previousPage = params.from;
  const isMopFlow = previousPage === 'mop-auth';
  const returnUrl = params.returnUrl;

  const correlationId = useMemo(
    () => getCorrelationId(applicationNumber, params.cid),
    [applicationNumber, params.cid],
  );

  // Every Lessee Portal API call made while this page is open carries X-Correlation-ID with this id.
  // A layout effect runs before the passive effects that make the page's first API calls.
  useLayoutEffect(() => {
    setActiveCorrelationId(correlationId);
    return () => clearActiveCorrelationId(correlationId);
  }, [correlationId]);

  // Determine flow entry point
  const getFlowEntryPoint = () => {
    if (returnUrl) return 'external_website';
    if (previousPage === 'mop-auth') return 'mop_authorization';
    return 'direct_payment_app';
  };
  const flowEntryPoint = getFlowEntryPoint();
  const mopLogger = useMemo(
    () => serverLogger.child({ correlationId, appNumber: applicationNumber, flowEntryPoint }),
    [correlationId, applicationNumber, flowEntryPoint],
  );

  const [application, setApplication] = useState(null);
  const [payixProfileId, setPayixProfileId] = useState(null);
  // Saved payment methods when the page opened. Null until PaymentSettings first loads them.
  const [savedPaymentMethodCount, setSavedPaymentMethodCount] = useState(null);
  const pageOpenedLoggedRef = useRef(false);
  const retryTimeoutRef = useRef(null);
  const [paymentSettingsMode, setPaymentSettingsMode] = useState('view'); // 'view', 'create', or 'update'
  const [dataLoadStatus, setDataLoadStatus] = useState(STEP_STATUS.idle);
  const [dataLoadAttempt, setDataLoadAttempt] = useState(0);
  const updateServerError = useCallback((message, context = null) => {
    setServerError(message);
    setErrorContext(message ? context : null);
  }, []);

  const isNetworkError = serverError === defaultNetworkErrorMessage;
  const authorizationReady = authStepOneStatus === STEP_STATUS.success;
  const isDataLoading = dataLoadStatus === STEP_STATUS.inProgress;
  const isDataLoaded = dataLoadStatus === STEP_STATUS.success;

  // Log network errors
  useEffect(() => {
    if (isNetworkError) {
      mopLogger.error(DD_PAYMENT_APP_EVENTS.ERROR_SCREEN_NETWORK, {
        appNumber: applicationNumber,
        flowStage: DD_PAYMENT_APP_EVENTS.ERROR_SCREEN_NETWORK,
        correlationId,
        flowEntryPoint,
        serverError,
        message: 'PaymentApplication: Network error - showing network error screen',
      });
    }
  }, [isNetworkError, applicationNumber, correlationId, flowEntryPoint, mopLogger, serverError]);

  // Log data load failures
  useEffect(() => {
    if (authorizationReady && dataLoadStatus === STEP_STATUS.failed && !isNetworkError) {
      mopLogger.error(DD_PAYMENT_APP_EVENTS.ERROR_SCREEN_DATA_LOAD, {
        appNumber: applicationNumber,
        flowStage: DD_PAYMENT_APP_EVENTS.ERROR_SCREEN_DATA_LOAD,
        correlationId,
        flowEntryPoint,
        serverError,
        message: 'PaymentApplication: Data load failed - showing error screen',
      });
    }
  }, [authorizationReady, dataLoadStatus, isNetworkError, applicationNumber, correlationId, flowEntryPoint, mopLogger, serverError]);
  useEffect(() => {
    return () => {
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
        retryTimeoutRef.current = null;
      }
    };
  }, []);

  const resetFlowState = () => {
    setApplication(null);
    setSelectedQuote(null);
    setPayixProfileId(null);
    setSelectedPaymentMethod(null);
    setProcessingFeeOption('lessee');
    updateServerError('');
    setDocusignUrl('');
    setFormError(null);
    setLoadProgress(0);
    setLoadStep('');
    setDataLoadStatus(STEP_STATUS.idle);
    setIsLoading(false);
    setDocumentGenerating(false);
  };

  // Log page view
  useEffect(() => {
    mopLogger.info(DD_PAYMENT_APP_EVENTS.PAGE_VIEW, {
      appNumber: applicationNumber,
      flowEntryPoint,
      flowStage: DD_PAYMENT_APP_EVENTS.PAGE_VIEW,
      message: 'PaymentApplication: Page view',
    });
  }, [applicationNumber, flowEntryPoint, mopLogger]);

  // Log the page opened line once, when the application and the saved payment methods are both loaded.
  useEffect(() => {
    if (pageOpenedLoggedRef.current || !isDataLoaded || savedPaymentMethodCount === null) return;
    pageOpenedLoggedRef.current = true;
    mopLogger.info(DD_PAYMENT_APP_EVENTS.PAGE_OPENED, {
      appNumber: applicationNumber,
      flowEntryPoint,
      flowStage: DD_PAYMENT_APP_EVENTS.PAGE_OPENED,
      hasCid: !!params.cid,
      savedPaymentMethodCount,
      isDirectDebitRequired,
      message: pageOpenedMessage({
        appNumber: applicationNumber,
        entrySource: describeEntrySource({ cid: params.cid, isMopFlow, returnUrl }),
        correlationId,
        savedPaymentMethodCount,
        isDirectDebitRequired,
      }),
    });
  }, [
    isDataLoaded,
    savedPaymentMethodCount,
    isDirectDebitRequired,
    applicationNumber,
    flowEntryPoint,
    correlationId,
    isMopFlow,
    returnUrl,
    params.cid,
    mopLogger,
  ]);

  // Log entry point once authenticated
  useEffect(() => {
    if (userName && applicationNumber) {
      mopLogger.info(DD_PAYMENT_APP_EVENTS.AUTH_SUCCESS, {
        userName,
        appNumber: applicationNumber,
        flowEntryPoint,
        hasReturnUrl: !!returnUrl,
        flowStage: DD_PAYMENT_APP_EVENTS.AUTH_SUCCESS,
        message: 'PaymentApplication: User authenticated',
      });
    }
  }, [userName, applicationNumber, flowEntryPoint, returnUrl, mopLogger, correlationId]);

  // Log authentication failures with detailed context
  useEffect(() => {
    if (authStepOneStatus === STEP_STATUS.failed) {
      const errorDetails = {
        appNumber: applicationNumber,
        errorType: authErrorType,
        correlationId,
        flowEntryPoint,
        authError,
      };

      if (authErrorType === AUTH_ERROR_TYPES.EXPIRED_TOKEN) {
        mopLogger.error(DD_PAYMENT_APP_EVENTS.ERROR_SCREEN_AUTH, {
          ...errorDetails,
          flowStage: DD_PAYMENT_APP_EVENTS.ERROR_SCREEN_AUTH,
          screenType: 'expired_token',
          message: 'PaymentApplication: Token expired - showing expired screen',
        });
      } else if (authErrorType === AUTH_ERROR_TYPES.INVALID_TOKEN) {
        mopLogger.error(DD_PAYMENT_APP_EVENTS.ERROR_SCREEN_AUTH, {
          ...errorDetails,
          flowStage: DD_PAYMENT_APP_EVENTS.ERROR_SCREEN_AUTH,
          screenType: 'invalid_token',
          message: 'PaymentApplication: Invalid token - showing invalid screen',
        });
      } else if (authErrorType === AUTH_ERROR_TYPES.MISSING_TOKEN) {
        mopLogger.warn(DD_PAYMENT_APP_EVENTS.ERROR_SCREEN_AUTH, {
          ...errorDetails,
          flowStage: DD_PAYMENT_APP_EVENTS.ERROR_SCREEN_AUTH,
          screenType: 'missing_token',
          message: 'PaymentApplication: Missing token - showing missing screen',
        });
      } else {
        mopLogger.error(DD_PAYMENT_APP_EVENTS.ERROR_SCREEN_AUTH, {
          ...errorDetails,
          flowStage: DD_PAYMENT_APP_EVENTS.ERROR_SCREEN_AUTH,
          screenType: 'unknown',
          message: 'PaymentApplication: Unknown auth error - showing fallback',
        });
      }
    }
  }, [authStepOneStatus, authErrorType, authError, applicationNumber, correlationId, flowEntryPoint, mopLogger]);

  const paymentMethodChangedHandler = (paymentMethod) => {
    // Check if this is a duplicate selection
    const isDuplicate = paymentMethod?.Id && selectedPaymentMethod?.Id === paymentMethod.Id;

    // Always update state, even if it's the same payment method
    setSelectedPaymentMethod(paymentMethod);

    // Only log when payment method actually changes (skip duplicate logs)
    if (!isDuplicate) {
      mopLogger.debug(DD_PAYMENT_APP_EVENTS.PAYMENT_METHOD_SELECTED, {
        userName,
        appNumber: applicationNumber,
        flowEntryPoint,
        flowStage: DD_PAYMENT_APP_EVENTS.PAYMENT_METHOD_SELECTED,
        paymentMethodId: paymentMethod?.Id,
        paymentMethodType: paymentMethod?.Type,
        message: 'PaymentApplication: Payment method selected',
      });
    }
  };
  useEffect(() => {
    // Create AbortController for request cancellation on unmount/cleanup
    const abortController = new AbortController();

    async function fetchData() {
      // Don't fetch data until both auth steps complete successfully
      if (!authorizationReady || !userName || authError) {
        if (!authorizationReady) {
          debugAuth('Authorization gate not complete, skipping data load');
        }
        if (!userName) {
          debugAuth('Waiting for authentication token...');
        }
        if (authError) {
          debugAuth('Auth error exists, skipping data fetch: %s', authError);
        }
        return;
      }

      // Validate application number is provided
      if (!applicationNumber) {
        debugError('Application number is missing from URL');
        updateServerError('Application number is required. Please check your link and try again.');
        setDataLoadStatus(STEP_STATUS.failed);
        setIsLoading(false);
        return;
      }

      debugAuth('Starting data fetch for user: %s, app: %s', userName, applicationNumber);

      // Verify auth data is available before making API calls
      const authData = Auth.getAuthData();
      if (!authData || !authData.token) {
        debugError('Auth data not available after authentication succeeded - this should not happen');
        updateServerError(APPROVED_SESSION_EXPIRED_MESSAGE);
        setDataLoadStatus(STEP_STATUS.failed);
        setIsLoading(false);
        return;
      }

      updateServerError('');
      setDataLoadStatus(STEP_STATUS.inProgress);
      setIsLoading(true);
      setLoadProgress(35);
      setLoadStep('Loading application...');

      // Log start of data loading
      mopLogger.info(DD_PAYMENT_APP_EVENTS.DATA_LOAD_START, {
        userName,
        appNumber: applicationNumber,
        flowEntryPoint,
        flowStage: DD_PAYMENT_APP_EVENTS.DATA_LOAD_START,
        message: 'PaymentApplication: Starting data load',
      });

      const getReadyDocsDocumentInfo = async (accountNumber) => {
        const url = `applications/${accountNumber}/readydocs-document`;
        try {
          if (accountNumber === '') return null;

          // Ensure we have auth data before making the call
          const authData = Auth.getAuthData();
          if (!authData || !authData.token) {
            debugError('No auth data available for getReadyDocsDocumentInfo');
            return null;
          }

          const config = {
            headers: { Authorization: `Bearer ${authData.token}` },
            signal: abortController.signal,
          };

          const response = await api.get(url, config);
          const readyDocsDocument = response.data;
          debugApi('getReadyDocsDocumentInfo response: %O', readyDocsDocument);
          return readyDocsDocument;
        } catch (error) {
          // Ignore abort errors - these are expected during cleanup
          if (error.name === 'AbortError' || error.code === 'ERR_CANCELED') {
            debugAuth('getReadyDocsDocumentInfo request cancelled');
            return null;
          }
          const msg = apiHelper.parseError(error);
          debugError('getReadyDocsDocumentInfo failed (non-blocking): %s', msg);

          // Log detailed ReadyDocs document load failure (non-blocking, optional data)
          mopLogger.warn(DD_PAYMENT_APP_EVENTS.DATA_LOAD_FAILED, {
            userName,
            appNumber: applicationNumber,
            flowEntryPoint,
            flowStage: DD_PAYMENT_APP_EVENTS.DATA_LOAD_FAILED,
            dataType: 'readydocs',
            endpoint: url,
            httpStatus: error.response?.status || null,
            errorType: error.name || error.code || 'UnknownError',
            errorMessage: msg,
            errorDetails: sanitizeErrorDetails(error),
            isTimeout: error.code === 'ECONNABORTED' || error.message?.includes('timeout'),
            isNetworkError: !error.response && error.request,
            isNonBlocking: true,
            correlationId,
            message: `PaymentApplication: Failed to load ReadyDocs document from endpoint ${url} (non-blocking). HTTP Status: ${error.response?.status || 'N/A'}, Error Type: ${error.name || error.code || 'Unknown'}`,
          });
          
          return null;
        }
      };
      const getApplicationRequirements = async (accountNumber) => {
        const url = `applications/${accountNumber}`;
        try {
          if (accountNumber === '') return;

          // Ensure we have auth data before making the call
          const authData = Auth.getAuthData();
          if (!authData || !authData.token) {
            debugError('No auth data available for getApplicationRequirements');
            mopLogger.error(DD_PAYMENT_APP_EVENTS.DATA_LOAD_FAILED, {
              userName,
              appNumber: applicationNumber,
              flowEntryPoint,
              flowStage: DD_PAYMENT_APP_EVENTS.DATA_LOAD_FAILED,
              dataType: 'application',
              reason: 'missing_auth_data',
              message: 'PaymentApplication: Missing auth data during application data load',
            });
            updateServerError(APPROVED_SESSION_EXPIRED_MESSAGE);
            setIsLoading(false);
            return;
          }

          const config = {
            headers: { Authorization: `Bearer ${authData.token}` },
            signal: abortController.signal,
          };

          let response = await api.get(url, config);
          let application = response.data;
          setApplication(application);
          setLoadProgress(50);
          setLoadStep('Loading quote...');
          _setAutoPaymentMessage(application);

          // Log successful application data load
          mopLogger.debug(DD_PAYMENT_APP_EVENTS.DATA_LOAD_STEP, {
            userName,
            appNumber: applicationNumber,
            flowEntryPoint,
            flowStage: DD_PAYMENT_APP_EVENTS.DATA_LOAD_STEP,
            dataType: 'application',
            hasApplication: !!application,
            message: 'PaymentApplication: Application data loaded',
          });

          return true;
        } catch (error) {
          // Ignore abort errors - these are expected during cleanup
          if (error.name === 'AbortError' || error.code === 'ERR_CANCELED') {
            debugAuth('getApplicationRequirements request cancelled');
            return false;
          }
          const errorMessage = apiHelper.parseError(error);
          // Check if this is an authentication error
          if (error.response && error.response.status === 401) {
            debugError('401 Unauthorized during data fetch - Session expired: %O', error);
            updateServerError(APPROVED_SESSION_EXPIRED_MESSAGE);
          } else {
            updateServerError(errorMessage);
          }
          debugError('getApplicationRequirements error: %O', error);

          // Log detailed application data load failure information
          mopLogger.error(DD_PAYMENT_APP_EVENTS.DATA_LOAD_FAILED, {
            userName,
            appNumber: applicationNumber,
            flowEntryPoint,
            flowStage: DD_PAYMENT_APP_EVENTS.DATA_LOAD_FAILED,
            dataType: 'application',
            endpoint: url,
            httpStatus: error.response?.status || null,
            errorType: error.name || error.code || 'UnknownError',
            errorMessage: errorMessage,
            errorDetails: sanitizeErrorDetails(error),
            isTimeout: error.code === 'ECONNABORTED' || error.message?.includes('timeout'),
            isNetworkError: !error.response && error.request,
            correlationId,
            message: `PaymentApplication: Failed to load application data from endpoint ${url}. HTTP Status: ${error.response?.status || 'N/A'}, Error Type: ${error.name || error.code || 'Unknown'}`,
          });

          return false;
        }
      };

      const getApplicationQuoteData = async (accountNumber) => {
        const url = `applications/${applicationNumber}/quote`;
        try {
          if (accountNumber === '') return;

          // Ensure we have auth data before making the call
          const authData = Auth.getAuthData();
          if (!authData || !authData.token) {
            debugError('No auth data available for getApplicationQuoteData');
            mopLogger.error(DD_PAYMENT_APP_EVENTS.DATA_LOAD_FAILED, {
              userName,
              appNumber: applicationNumber,
              flowEntryPoint,
              flowStage: DD_PAYMENT_APP_EVENTS.DATA_LOAD_FAILED,
              dataType: 'quote',
              reason: 'missing_auth_data',
              message: 'PaymentApplication: Missing auth data during quote data load',
            });
            updateServerError(APPROVED_SESSION_EXPIRED_MESSAGE);
            setDataLoadStatus(STEP_STATUS.failed);
            setIsLoading(false);
            return false;
          }

          const config = {
            headers: { Authorization: `Bearer ${authData.token}` },
            signal: abortController.signal,
          };

          const response = await api.get(url, config);
          setSelectedQuote(response.data);
          setLoadProgress(80);
          setLoadStep('Checking documents...');

          // Log successful quote data load
          mopLogger.debug(DD_PAYMENT_APP_EVENTS.DATA_LOAD_STEP, {
            userName,
            appNumber: applicationNumber,
            flowEntryPoint,
            flowStage: DD_PAYMENT_APP_EVENTS.DATA_LOAD_STEP,
            dataType: 'quote',
            hasQuote: !!response.data,
            message: 'PaymentApplication: Quote data loaded',
          });

          return true;
        } catch (error) {
          // Ignore abort errors - these are expected during cleanup
          if (error.name === 'AbortError' || error.code === 'ERR_CANCELED') {
            debugAuth('getApplicationQuoteData request cancelled');
            return false;
          }
          const errorMessage = apiHelper.parseError(error);
          // Check if this is an authentication error
          if (error.response && error.response.status === 401) {
            debugError('401 Unauthorized during data fetch - Session expired: %O', error);
            updateServerError(APPROVED_SESSION_EXPIRED_MESSAGE);
          } else {
            updateServerError(errorMessage);
          }
          debugError('getApplicationQuoteData error: %O', error);

          // Log detailed quote data load failure information
          mopLogger.error(DD_PAYMENT_APP_EVENTS.DATA_LOAD_FAILED, {
            userName,
            appNumber: applicationNumber,
            flowEntryPoint,
            flowStage: DD_PAYMENT_APP_EVENTS.DATA_LOAD_FAILED,
            dataType: 'quote',
            endpoint: url,
            httpStatus: error.response?.status || null,
            errorType: error.name || error.code || 'UnknownError',
            errorMessage: errorMessage,
            errorDetails: sanitizeErrorDetails(error),
            isTimeout: error.code === 'ECONNABORTED' || error.message?.includes('timeout'),
            isNetworkError: !error.response && error.request,
            correlationId,
            message: `PaymentApplication: Failed to load quote data from endpoint ${url}. HTTP Status: ${error.response?.status || 'N/A'}, Error Type: ${error.name || error.code || 'Unknown'}`,
          });

          return false;
        }
      };
      const requirementsLoaded = await getApplicationRequirements(applicationNumber);
      const quoteLoaded = await getApplicationQuoteData(applicationNumber);

      if (!requirementsLoaded || !quoteLoaded) {
        setDataLoadStatus(STEP_STATUS.failed);
        setIsLoading(false);
        setLoadProgress(100);
        setLoadStep('Ready');
        return;
      }
      var readyDocsRecord = await getReadyDocsDocumentInfo(applicationNumber);
      debugApp('fetchData readyDocsRecord: %O', readyDocsRecord);
      if (readyDocsRecord) {
        setProcessingFeeOption(readyDocsRecord?.ProcessingFeeOption);

        // Only pre-populate PayixProfileId if NOT coming from MOP authorization flow
        // This forces user to explicitly select/add payment method for MOP corrections
        if (!isMopFlow) {
          setPayixProfileId(readyDocsRecord?.PayixProfileId);

          mopLogger.debug(DD_PAYMENT_APP_EVENTS.DATA_LOAD_STEP, {
            userName,
            appNumber: applicationNumber,
            flowEntryPoint,
            flowStage: DD_PAYMENT_APP_EVENTS.DATA_LOAD_STEP,
            dataType: 'readydocs',
            cacheResult: 'used',
            payixProfileId: readyDocsRecord?.PayixProfileId,
            message: 'PaymentApplication: Using cached PayixProfileId from ReadyDocs',
          });
        } else {
          mopLogger.debug(DD_PAYMENT_APP_EVENTS.DATA_LOAD_STEP, {
            userName,
            appNumber: applicationNumber,
            flowEntryPoint,
            flowStage: DD_PAYMENT_APP_EVENTS.DATA_LOAD_STEP,
            dataType: 'readydocs',
            cacheResult: 'skipped',
            message: 'PaymentApplication: Skipping cached PayixProfileId for MOP flow',
          });
        }
      }
      // Ensure loading is complete
      setIsLoading(false);
      setDataLoadStatus(STEP_STATUS.success);
      setLoadProgress(100);
      setLoadStep('Ready');
      debugApp('Data fetch complete, form should now be visible');

      // Log when all data is loaded and form is ready
      mopLogger.info(DD_PAYMENT_APP_EVENTS.DATA_LOAD_SUCCESS, {
        userName,
        appNumber: applicationNumber,
        flowEntryPoint,
        flowStage: DD_PAYMENT_APP_EVENTS.DATA_LOAD_SUCCESS,
        hasReadyDocsRecord: !!readyDocsRecord,
        hasPayixProfileId: !!readyDocsRecord?.PayixProfileId,
        message: 'PaymentApplication: Data load complete',
      });
    }

    fetchData();

    // Cleanup: abort all pending requests when component unmounts or dependencies change
    return () => {
      debugAuth('Aborting pending data fetch requests');
      abortController.abort();
    };
  }, [authorizationReady, applicationNumber, userName, authError, dataLoadAttempt, flowEntryPoint, mopLogger]);

  useEffect(() => {
    const _setAmountDueAtSigningMessage = () => {
      if (selectedQuote == null) return;
      let isPayableAtSigningGreaterThanZero = selectedQuote?.PayableAtSigning > 0;

      setIsDueAtSigningGreaterThanZero(isPayableAtSigningGreaterThanZero);

      const amountDue =
        processingFeeOption?.toLowerCase() === 'lessee'
          ? selectedQuote?.PayableAtSigning?.toFixed(2)
          : selectedQuote?.PayableAtSigningDealer?.toFixed(2);
      const text = `The amount due at signing is estimated to be $${amountDue}. You must authorize TimePayment to charge this amount prior to commencing the lease.`;
      let message = isPayableAtSigningGreaterThanZero ? text : 'No payment is due at signing.';

      setAmountDueAtSigningMessage(message);
    };
    _setAmountDueAtSigningMessage();
  }, [selectedQuote, processingFeeOption]);

  const _setAutoPaymentMessage = (application) => {
    const requirements = application.Requirements;
    const hasNonWaivedRequirement =
      requirements.filter((r) => r.Requirement__c === 'Direct Debit Required' && r.Status__c !== 'Waived').length > 0;
    setIsDirectDebitRequired(hasNonWaivedRequirement);
    if (hasNonWaivedRequirement) {
      mopLogger.info(DD_PAYMENT_APP_EVENTS.DIRECT_DEBIT_REQUIRED, {
        appNumber: applicationNumber,
        flowEntryPoint,
        flowStage: DD_PAYMENT_APP_EVENTS.DIRECT_DEBIT_REQUIRED,
        isDirectDebitRequired: true,
        message: directDebitRequiredMessage(applicationNumber),
      });
    }
    // const hasWaivedStatusRequirement = requirements.filter((r) => r.Status__c == 'Waived').length > 0;
    const messageCondition = !hasNonWaivedRequirement;
    const message = messageCondition
      ? 'We encourage automatic payments to prevent any statement billing fees (if applicable) or late fees due to missing a payment.'
      : 'Automatic payments are required for this lease.';
    setAutomaticPaymentMessage(message);
  };
  const onGenerateDocument = async () => {
    if (!selectedPaymentMethod) {
      setFormError('Please select Payment Method');

      // Log validation failure - user tried to submit without payment method (normal case)
      mopLogger.debug(DD_PAYMENT_APP_EVENTS.VALIDATION_FAILED, {
        userName,
        appNumber: applicationNumber,
        flowEntryPoint,
        flowStage: DD_PAYMENT_APP_EVENTS.VALIDATION_FAILED,
        reason: 'no_payment_method',
        message: 'PaymentApplication: Validation - no payment method selected (user input)',
      });
      return;
    }

    // MethodOfPayment carries the Automatic Payment checkbox: ACH when checked, Statement when not.
    // EmbeddedSigningReturnUrl is included only when returnUrl is provided.
    const payload = buildDocumentSignPayload({
      processingFeeOption,
      paymentProfileId: selectedPaymentMethod.Id,
      isAutoPay,
      returnUrl,
    });

    mopLogger.info(DD_PAYMENT_APP_EVENTS.METHOD_OF_PAYMENT_SELECTED, {
      appNumber: applicationNumber,
      flowEntryPoint,
      flowStage: DD_PAYMENT_APP_EVENTS.METHOD_OF_PAYMENT_SELECTED,
      methodOfPayment: payload.MethodOfPayment,
      isAutoPay,
      isDirectDebitRequired,
      message: selectionMessage(applicationNumber, payload.MethodOfPayment),
    });

    // Log document generation start
    mopLogger.info(DD_PAYMENT_APP_EVENTS.DOCUMENT_GEN_START, {
      userName,
      appNumber: applicationNumber,
      flowEntryPoint,
      flowStage: DD_PAYMENT_APP_EVENTS.DOCUMENT_GEN_START,
      paymentMethodId: selectedPaymentMethod.Id,
      message: 'PaymentApplication: Starting document generation',
    });

    setIsLoading(true);
    setDocumentGenerating(true);
    setLoadProgress(10);
    setLoadStep('Preparing document...');
    const url = `applications/${applicationNumber}/document-sign`;
    try {
      // Ensure we have auth data before making the call
      const authData = Auth.getAuthData();
      if (!authData || !authData.token) {
        debugError('No auth data available for onGenerateDocument');
        setIsLoading(false);
        setDocumentGenerating(false);
        setLoadProgress(100);
        setLoadStep('Ready');
        setFormError('Authentication required. Please refresh the page.');
        return;
      }

      setLoadProgress(50);
      setLoadStep('Generating document...');

      const config = {
        responseType: 'json',
        headers: { Authorization: `Bearer ${authData.token}` },
      };

      const response = await api.post(url, payload, config);
      debugApi('onGenerateDocument response: %O', response);

      const { data, status } = response;
      debugApi('onGenerateDocument response data: %O', data);

      setLoadProgress(90);
      setLoadStep('Document ready...');

      setDocumentGenerating(false);
      if (status === 200 && data?.ViewUrl) {
        // Log document generation success
        mopLogger.info(DD_PAYMENT_APP_EVENTS.DOCUMENT_GEN_SUCCESS, {
          userName,
          appNumber: applicationNumber,
          flowEntryPoint,
          flowStage: DD_PAYMENT_APP_EVENTS.DOCUMENT_GEN_SUCCESS,
          hasDocusignUrl: !!data.ViewUrl,
          message: 'PaymentApplication: Document generated successfully',
        });

        // Log DocuSign redirect (critical success milestone)
        mopLogger.info(DD_PAYMENT_APP_EVENTS.DOCUSIGN_REDIRECT, {
          userName,
          appNumber: applicationNumber,
          flowEntryPoint,
          flowStage: DD_PAYMENT_APP_EVENTS.DOCUSIGN_REDIRECT,
          envelopeId: data.EnvelopeId || null,
          message: envelopeReturnedMessage(applicationNumber, data.EnvelopeId),
        });

        setDocusignUrl(data.ViewUrl);
      }
      //getPdf(response.data, `Lease_${applicationNumber}.pdf`);
      setIsLoading(false);
      setLoadProgress(100);
      setLoadStep('Complete');
    } catch (error) {
      setIsLoading(false);
      setDocumentGenerating(false);
      setLoadProgress(100);
      setLoadStep('Ready');
      const message = apiHelper.parseError(error);

      if (error.response?.status === 400) {
        // The API returns ReadyDocs' 400 message. The banner below shows it and the lessee stays on the page.
        mopLogger.warn(DD_PAYMENT_APP_EVENTS.DOCUMENT_SIGN_REJECTED, {
          userName,
          appNumber: applicationNumber,
          flowEntryPoint,
          flowStage: DD_PAYMENT_APP_EVENTS.DOCUMENT_SIGN_REJECTED,
          endpoint: url,
          httpStatus: 400,
          errorMessage: message,
          paymentMethodId: selectedPaymentMethod?.Id || null,
          methodOfPayment: payload.MethodOfPayment,
          message: signRejectedMessage(applicationNumber, 400, message),
        });
      } else {
        // Log detailed document generation failure information
        mopLogger.error(DD_PAYMENT_APP_EVENTS.DOCUMENT_GEN_FAILED, {
        userName,
        appNumber: applicationNumber,
        flowEntryPoint,
        flowStage: DD_PAYMENT_APP_EVENTS.DOCUMENT_GEN_FAILED,
        endpoint: url,
        httpStatus: error.response?.status || null,
        errorType: error.name || error.code || 'UnknownError',
        errorMessage: message,
        errorDetails: sanitizeErrorDetails(error),
        isTimeout: error.code === 'ECONNABORTED' || error.message?.includes('timeout'),
        isNetworkError: !error.response && error.request,
        paymentMethodId: selectedPaymentMethod?.Id || null,
        processingFeeOption: processingFeeOption,
        correlationId,
        message: `PaymentApplication: Document generation failed at endpoint ${url}. HTTP Status: ${error.response?.status || 'N/A'}, Error Type: ${error.name || error.code || 'Unknown'}`,
      });
      }
      const context =
        typeof message === 'string' &&
        message.toLowerCase().includes('method of payment') &&
        message.toLowerCase().includes('confirm')
          ? 'mopVerification'
          : null;
      updateServerError(message, context);
    }
  };

  // Compute disabled state for UI interactions
  const isUiDisabled = isAuthenticating || isLoading || documentGenerating || !userName || !isDataLoaded;

  // Keep DocuSign iframe takeover when available
  if (docusignUrl) {
    const el = document.getElementById('root');
    if (el) el.remove();

    return createPortal(
      <iframe
        id="insync-iframe"
        title="insync-iframe"
        src={docusignUrl}
        style={frameStyle}
        referrerPolicy="origin"
        sandbox="allow-scripts allow-modals allow-popups allow-same-origin allow-top-navigation"
      >
        <p>Your browser does not support iframes.</p>
      </iframe>,
      document.body,
    );
  }
  const showSuccessMessage = async () => {
    try {
      // Set loading states for MOP auth flow
      setIsLoading(true);
      setLoadProgress(20);
      setLoadStep('Processing...');

      // Create MOP progress record      // Ensure we have auth data before making the call
      const authData = Auth.getAuthData();
      if (!authData || !authData.token) {
        debugError('No auth data available for showSuccessMessage');
        const message = 'Authentication required. Please refresh the page.';
        updateServerError(message);
        setIsLoading(false);
        setLoadProgress(100);
        setLoadStep('Ready');
        return;
      }

      setLoadProgress(60);
      setLoadStep('Creating progress record...');

      const config = {
        headers: { Authorization: `Bearer ${authData.token}` },
      };

      mopLogger.info(DD_PAYMENT_APP_EVENTS.MOP_PROGRESS_START, {
        userName,
        appNumber: applicationNumber,
        flowStage: DD_PAYMENT_APP_EVENTS.MOP_PROGRESS_START,
        flowEntryPoint,
        paymentMethodSelected: !!selectedPaymentMethod,
        message: 'MOP Flow: Creating progress record',
      });

      await api.post(`applications/${applicationNumber}/create-mop-progress`, {}, config);

      setLoadProgress(75);
      setLoadStep('Verifying your submission...');

      mopLogger.info(DD_PAYMENT_APP_EVENTS.MOP_PROGRESS_SUCCESS, {
        userName,
        appNumber: applicationNumber,
        flowStage: DD_PAYMENT_APP_EVENTS.MOP_PROGRESS_SUCCESS,
        flowEntryPoint,
        message: 'MOP Flow: Progress record created successfully',
      });

      setLoadProgress(90);
      setLoadStep('Redirecting...');

      // Redirect to success message page on success
      const successUrl = appendCidToUrl(`/payment-method-success?app=${applicationNumber}`, correlationId);
      history.push(successUrl);
    } catch (error) {
      debugError('Error creating MOP progress: %O', error);
      // Handle error - could show error message or still redirect
      const message = apiHelper.parseError(error);
      const endpoint = `applications/${applicationNumber}/create-mop-progress`;
      mopLogger.error(DD_PAYMENT_APP_EVENTS.MOP_PROGRESS_FAILED, {
        userName,
        appNumber: applicationNumber,
        flowStage: DD_PAYMENT_APP_EVENTS.MOP_PROGRESS_FAILED,
        flowEntryPoint,
        endpoint: endpoint,
        httpStatus: error.response?.status || null,
        errorType: error.name || error.code || 'UnknownError',
        errorMessage: message,
        errorDetails: sanitizeErrorDetails(error),
        isTimeout: error.code === 'ECONNABORTED' || error.message?.includes('timeout'),
        isNetworkError: !error.response && error.request,
        paymentMethodSelected: !!selectedPaymentMethod,
        paymentMethodId: selectedPaymentMethod?.Id || null,
        correlationId,
        message: `MOP Flow: Failed to create progress record at endpoint ${endpoint}. HTTP Status: ${error.response?.status || 'N/A'}, Error Type: ${error.name || error.code || 'Unknown'}`,
      });

      // Determine error context for special handling
      const context =
        typeof message === 'string' &&
        message.toLowerCase().includes('payment method') &&
        message.toLowerCase().includes('payment system')
          ? 'payixVerification'
          : typeof message === 'string' && message.toLowerCase().includes('salesforce')
          ? 'mopVerification'
          : null;

      updateServerError(message, context);
      setIsLoading(false);
      setLoadProgress(100);
      setLoadStep('Ready');
    }
  };

  const retryDataLoad = () => {
    resetFlowState();
    setDataLoadAttempt((count) => count + 1);
  };

  const retryVerification = async () => {
    updateServerError('');
    await showSuccessMessage();
  };

  // PaymentSettings reports the saved payment methods each time it loads them. The first load is the count at page open.
  const onPaymentMethodsLoaded = (count) => {
    setSavedPaymentMethodCount((previous) => (previous === null ? count : previous));
  };

  // PaymentSettings reports a method added in Payix, or null when it was not found in the refreshed list.
  const onPaymentMethodAdded = (method) => {
    if (method) {
      mopLogger.info(DD_PAYMENT_APP_EVENTS.PAYMENT_METHOD_ADDED, {
        appNumber: applicationNumber,
        flowEntryPoint,
        flowStage: DD_PAYMENT_APP_EVENTS.PAYMENT_METHOD_ADDED,
        paymentMethodId: method.Id,
        paymentMethodType: method.PaymentType,
        lastFour: method.LastFour || null,
        message: paymentMethodAddedMessage(applicationNumber, method),
      });
    } else {
      mopLogger.warn(DD_PAYMENT_APP_EVENTS.PAYMENT_METHOD_ADDED_NOT_FOUND, {
        appNumber: applicationNumber,
        flowEntryPoint,
        flowStage: DD_PAYMENT_APP_EVENTS.PAYMENT_METHOD_ADDED_NOT_FOUND,
        message: paymentMethodAddedNotFoundMessage(applicationNumber),
      });
    }
  };

  const onPaymentSettingsModeChanged = (mode) => {
    setPaymentSettingsMode(mode);

    // Log when user initiates adding a new payment method
    if (mode === 'create') {
      mopLogger.debug(DD_PAYMENT_APP_EVENTS.ADD_PAYMENT_METHOD_CLICKED, {
        userName,
        appNumber: applicationNumber,
        flowEntryPoint,
        flowStage: DD_PAYMENT_APP_EVENTS.ADD_PAYMENT_METHOD_CLICKED,
        message: 'PaymentApplication: User clicked Add Payment Method',
      });
    }
  };
  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError(null);
    setIsSubmitting(true);

    // Log submit button click
    mopLogger.info(DD_PAYMENT_APP_EVENTS.SUBMIT_CLICKED, {
      userName,
      appNumber: applicationNumber,
      flowEntryPoint,
      flowStage: DD_PAYMENT_APP_EVENTS.SUBMIT_CLICKED,
      hasPaymentMethod: !!selectedPaymentMethod,
      message: 'PaymentApplication: Submit button clicked',
    });

    // Scroll to top to show progress indicator, especially important on mobile
    window.scrollTo({ top: 0, behavior: 'smooth' });

    try {
      if (previousPage === 'mop-auth') {
        await showSuccessMessage();
      } else {
        await onGenerateDocument();
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const showDataLoading = authorizationReady && isDataLoading;
  const showProgressBar = authorizationReady && (isLoading || documentGenerating);
  const canShowForm = authorizationReady && isDataLoaded && !documentGenerating && !isLoading;

  if (isNetworkError) {
    return (
       <div className="alert alert-info alert-centered" role="alert">
        <h4 className="alert-heading">We couldn&apos;t reach the server</h4>
        <p>{APPROVED_PAYMENT_SERVICE_UNAVAILABLE_MESSAGE}</p>
        <div className="toolbar toolbar-centered">
          <button className="btn btn-primary" onClick={authorizationReady ? retryDataLoad : () => window.location.reload()}>
            Retry
          </button>
        </div>
      </div>
    );
  }

  // Show authentication progress
  if (isAuthenticating || authStepOneStatus === STEP_STATUS.inProgress) {
    return (
      <div className="content-centered">
        <div className="payment-progress-bar">
          <div className="progress">
            <div
              className="progress-bar progress-bar-striped active"
              role="progressbar"
              aria-valuenow={authLoadProgress}
              aria-valuemin="0"
              aria-valuemax="100"
              style={{ width: `${authLoadProgress}%` }}
            >
              {authLoadStep || 'Authenticating...'}
            </div>
          </div>
        </div>
        <h2>Verifying Access</h2>
        <p>Please wait while we verify your secure link.</p>
      </div>
    );
  }

  // Show error screens if authentication fails
  if (authStepOneStatus === STEP_STATUS.failed) {
    if (authErrorType === AUTH_ERROR_TYPES.INVALID_TOKEN) {
      return <InvalidTokenScreen />;
    }

    if (authErrorType === AUTH_ERROR_TYPES.EXPIRED_TOKEN) {
      return <ExpiredTokenScreen />;
    }

    if (authErrorType === AUTH_ERROR_TYPES.MISSING_TOKEN) {
      return <MissingTokenScreen />;
    }

    // Default fallback for any other error
    return (
      <div className="alert alert-danger alert-centered" role="alert">
        <h4 className="alert-heading">Authorization Issue</h4>
        <p>{authError || 'We could not verify your secure link. Please refresh this page and try again. If it still does not work, request a new link from your representative.'}</p>
      </div>
    );
  }

  if (showDataLoading) {
    return (
      <div className="content-centered">
        <div className="payment-progress-bar">
          <div className="progress">
            <div
              className="progress-bar progress-bar-striped active"
              role="progressbar"
              aria-valuenow={loadProgress}
              aria-valuemin="0"
              aria-valuemax="100"
              style={{ width: `${loadProgress}%` }}
            >
              {loadStep || 'Loading data...'}
            </div>
          </div>
        </div>
        <h2>Loading Application</h2>
        <p>Please wait while we load your payment application details.</p>
      </div>
    );
  }

  if (authorizationReady && dataLoadStatus === STEP_STATUS.failed) {
    return (
      <div className="alert alert-danger alert-centered" role="alert">
        <h4 className="alert-heading">We couldn&apos;t load your application</h4>
        <p>{serverError || 'Unable to load application data. Please try again. If the problem continues, request a fresh link from your representative.'}</p>
        {serverError && !serverError.includes('session has expired') && (
          <details className="error-details">
            <summary>Technical Details</summary>
            <pre>{serverError}</pre>
          </details>
        )}
      </div>
    );
  }

  return (
    <Suspense fallback="loading">
      <div>
        {serverError && dataLoadStatus !== STEP_STATUS.failed ? (
          <div
            className={`alert ${
              errorContext === 'mopVerification'
                ? 'alert-warning'
                : errorContext === 'payixVerification'
                ? 'alert-danger'
                : 'alert-danger'
            }`}
            role="alert"
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: '1rem', alignItems: 'flex-start' }}>
              <div>
                <strong>
                  {errorContext === 'mopVerification'
                    ? 'Unable to complete progress update'
                    : errorContext === 'payixVerification'
                    ? 'Unable to verify payment method'
                    : 'We ran into a problem.'}
                </strong>
                <div>{serverError}</div>
                {errorContext === 'payixVerification' && isMopFlow ? (
                  <small className="text-muted">
                    We were unable to verify your payment method, so your submission is incomplete. Please try again, or contact support if this persists. Reference this error when contacting support.
                  </small>
                ) : errorContext === 'mopVerification' && isMopFlow ? (
                  <small className="text-muted">
                    We are still processing your submission. This usually resolves within a few moments. Please try
                    again, or contact support if this continues.
                  </small>
                ) : null}
              </div>
              <div className="btn-toolbar" style={{ gap: '0.25rem' }}>
                {(errorContext === 'mopVerification' || errorContext === 'payixVerification') && isMopFlow ? (
                  <button
                    className="btn btn-link"
                    type="button"
                    onClick={() => retryVerification()}
                    disabled={isLoading}
                  >
                    Retry verification
                  </button>
                ) : null}
                <button
                  className="btn btn-link"
                  type="button"
                  onClick={() => updateServerError('')}
                  style={{ textDecoration: 'none' }}
                >
                  Dismiss
                </button>
              </div>
            </div>
          </div>
        ) : null}

        {showProgressBar && (
          <div className="payment-progress-bar">
            <div className="progress">
              <div
                className="progress-bar progress-bar-striped active"
                role="progressbar"
                aria-valuenow={loadProgress}
                aria-valuemin="0"
                aria-valuemax="100"
                style={{ width: `${loadProgress}%` }}
              >
                {loadStep}
              </div>
            </div>
          </div>
        )}

        {documentGenerating && (
          <div className="content-centered">
            <h2>Generating Your Document</h2>
            <p>Please wait while we prepare your lease documentation...</p>
          </div>
        )}

        {canShowForm && (
          <>
            <h2>Enter Your Payment Method</h2>

            <p>Please select your payment method for any payments due at signing and ongoing scheduled payments.</p>

            <p>
              <span className="reminder-label">REMINDER:</span> The Method of Payment you enter must be in the name of
              either the Business or the Primary Guarantor on the application.
            </p>
            <section>
              <label>
                <input
                  key="apKey"
                  type="checkbox"
                  checked={isAutoPay}
                  disabled={isUiDisabled || isDirectDebitRequired}
                  onChange={(e) => setIsAutoPay(e.target.checked)}
                />
                I authorize TimePayment to automatically withdraw payments due via EFT or Credit Card.
              </label>

              {automaticPaymentMessage ? (
                <div>
                  <WarningLabel>{automaticPaymentMessage}</WarningLabel>
                </div>
              ) : null}
            </section>

            <section style={{ marginTop: '2rem' }}>
              <label>
                <input key="adsKey" type="checkbox" checked={!!(selectedQuote?.PayableAtSigning > 0)} disabled readOnly />I
                authorize TimePayment to withdraw any payments due at signing via EFT or Credit Card.
              </label>

              {isDueAtSigningGreaterThanZero != null ? (
                <div>
                  <WarningLabel>{amountDueAtSigningMessage}</WarningLabel>
                </div>
              ) : null}
            </section>
            <div className={isUiDisabled ? 'form-disabled' : ''}>
              <BorderBox>
                <PaymentSettings
                  userName={userName}
                  accountnumber={applicationNumber}
                  correlationId={correlationId}
                  // LESSEEPORT-1594: the shared api interceptor loses the Authorization
                  // header in this flow; pass the one-time-link key so payment write
                  // calls send it explicitly (URL key survives storage corruption).
                  authToken={params.key || params.token}
                  payixProfileId={payixProfileId}
                  onPaymentMethodChanged={paymentMethodChangedHandler}
                  onPaymentMethodsLoaded={onPaymentMethodsLoaded}
                  onPaymentMethodAdded={onPaymentMethodAdded}
                  onModeChanged={onPaymentSettingsModeChanged}
                  hideUpdateButtons
                  selectedAccount={{
                    PaymentFrequency: application?.PaymentFrequency,
                    PaymentMethod: application?.PaymentMethod,
                  }}
                />
              </BorderBox>
            </div>
            {formError ? (
              <div className="alert alert-danger" role="alert" style={{ marginTop: '1rem' }}>
                {formError}
              </div>
            ) : null}
            {
              paymentSettingsMode === 'create' || paymentSettingsMode === 'update' || !selectedPaymentMethod ? (
                <div style={{ marginTop: '1rem' }}>
                  <WarningLabel>
                    {paymentSettingsMode === 'create' || paymentSettingsMode === 'update'
                      ? 'Please complete adding your payment method before submitting.'
                      : 'Please select a payment method to continue.'}
                  </WarningLabel>
                </div>
              ) : (
                <div className="toolbar">
                  <button className="btn btn-primary" disabled={isUiDisabled || isSubmitting} onClick={handleSubmit}>
                    {isSubmitting ? <SpinnerText loading text="Submitting..." /> : 'Submit'}
                  </button>
                </div>
              )
            }
          </>
        )}
      </div>
    </Suspense>
  );
};
export default connect(
  (state) => state.login,
  (dispatch) => bindActionCreators(actionCreators, dispatch),
)(PaymentApplication);
